/**
 * Aircraft Cockpit Toggle Switch Component
 * Realistic metal lever toggle like Boeing/Airbus cockpit
 * BIGGER SIZE - NO CLICK HANDLER (parent manages it)
 */
import { useState } from 'react';

const CockpitToggle = ({ 
  isOn = false, 
  label = '',
  sublabel = '',
  onToggle,  // Optional - only used when component manages its own click
  disabled = false,
  size = 'medium' // 'small', 'medium', 'large', 'xlarge'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || !onToggle) return;
    setIsAnimating(true);
    onToggle();
    setTimeout(() => setIsAnimating(false), 200);
  };

  const sizes = {
    mini: { switch: 'w-8 h-12', lever: 'w-4 h-5', label: 'text-[8px]', led: 'w-1.5 h-1.5' },
    small: { switch: 'w-10 h-16', lever: 'w-6 h-8', label: 'text-[10px]', led: 'w-2 h-2' },
    medium: { switch: 'w-12 h-20', lever: 'w-7 h-10', label: 'text-xs', led: 'w-2.5 h-2.5' },
    large: { switch: 'w-16 h-28', lever: 'w-9 h-14', label: 'text-sm', led: 'w-3 h-3' },
    xlarge: { switch: 'w-20 h-32', lever: 'w-12 h-16', label: 'text-base', led: 'w-4 h-4' }
  };
  
  const s = sizes[size] || sizes.large;

  // If onToggle provided, component handles click internally
  // Otherwise, it's just a display component
  const Component = onToggle ? 'button' : 'div';

  return (
    <div className="flex flex-col items-center">
      {/* Label above */}
      {label && (
        <div className={`${s.label} font-mono text-amber-200/80 uppercase tracking-wider mb-1 text-center max-w-[140px] truncate font-bold`} title={label}>
          {label}
        </div>
      )}
      
      {/* Switch housing */}
      <Component
        onClick={onToggle ? handleClick : undefined}
        disabled={disabled}
        type={onToggle ? "button" : undefined}
        className={`relative ${s.switch} rounded-sm transition-all ${disabled ? 'opacity-40 cursor-not-allowed' : onToggle ? 'cursor-pointer hover:brightness-110 active:scale-95' : ''}`}
        style={{
          background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 50%, #1a1a1a 100%)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.8), inset 0 -2px 4px rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.5)',
          border: '2px solid #333'
        }}
        data-testid={`cockpit-toggle-${label?.toLowerCase().replace(/\s+/g, '-') || 'switch'}`}
      >
        {/* Switch track groove */}
        <div 
          className="absolute inset-x-2 top-2 bottom-2 rounded-sm"
          style={{
            background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
            boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.9)'
          }}
        />
        
        {/* Metal lever */}
        <div 
          className={`absolute left-1/2 -translate-x-1/2 ${s.lever} rounded-sm transition-all duration-150 ${isAnimating ? 'scale-90' : ''}`}
          style={{
            top: isOn ? '8px' : 'auto',
            bottom: isOn ? 'auto' : '8px',
            background: `linear-gradient(${isOn ? '180deg' : '0deg'}, 
              #8b7355 0%, 
              #a08060 15%, 
              #d4b896 35%,
              #c9a66b 50%, 
              #a08060 65%, 
              #8b7355 85%, 
              #6b5a45 100%)`,
            boxShadow: isOn 
              ? '0 6px 12px rgba(0,0,0,0.6), inset 0 3px 6px rgba(255,255,255,0.4), inset 0 -3px 6px rgba(0,0,0,0.3)' 
              : '0 -6px 12px rgba(0,0,0,0.6), inset 0 -3px 6px rgba(255,255,255,0.4), inset 0 3px 6px rgba(0,0,0,0.3)',
            border: '2px solid #5a4a35'
          }}
        >
          {/* Lever grip ridges */}
          <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 space-y-1">
            <div className="h-0.5 bg-black/40 rounded" />
            <div className="h-0.5 bg-white/30 rounded" />
            <div className="h-0.5 bg-black/40 rounded" />
            <div className="h-0.5 bg-white/30 rounded" />
            <div className="h-0.5 bg-black/40 rounded" />
          </div>
        </div>
        
        {/* ON/OFF indicators - bigger */}
        <div 
          className="absolute -left-3 top-3 w-2 h-3 rounded-sm" 
          style={{ 
            background: isOn ? '#4ade80' : '#1a1a1a', 
            boxShadow: isOn ? '0 0 10px #4ade80, 0 0 15px #4ade80' : 'none' 
          }} 
        />
        <div 
          className="absolute -left-3 bottom-3 w-2 h-3 rounded-sm" 
          style={{ 
            background: !isOn ? '#ef4444' : '#1a1a1a', 
            boxShadow: !isOn ? '0 0 10px #ef4444, 0 0 15px #ef4444' : 'none' 
          }} 
        />
      </Component>
      
      {/* Status LED - bigger */}
      <div 
        className={`mt-3 ${s.led} rounded-full transition-all`}
        style={{
          background: isOn 
            ? 'radial-gradient(circle, #fbbf24 0%, #f59e0b 50%, #d97706 100%)' 
            : 'radial-gradient(circle, #374151 0%, #1f2937 100%)',
          boxShadow: isOn ? '0 0 12px #fbbf24, 0 0 24px #f59e0b' : 'inset 0 1px 3px rgba(0,0,0,0.5)'
        }}
      />
      
      {/* Sublabel */}
      {sublabel && (
        <div className={`${s.label} font-mono text-gray-500 mt-1 text-center max-w-[140px] truncate`} title={sublabel}>
          {sublabel}
        </div>
      )}
    </div>
  );
};

export default CockpitToggle;
